import { useEffect, useRef, useState } from "react";
import { Images, Upload, Loader2, MapPin, CalendarClock, Tag, MessageCircle, Pencil, Trash2, X, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Modal, inp, lbl } from "../gear/GearBits";

/* Trip Photos Album — per-photo title, description, taken date/time, location
   (text + GPS auto-read from EXIF; PhotoPulse.pro API integration planned), tags & comments. */
const src = (u) => (u?.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL + u : u);

export default function TripAlbum({ trip }) {
  const [photos, setPhotos] = useState([]);
  const [role, setRole] = useState("owner");
  const [open, setOpen] = useState(null); // photo in lightbox
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/album/${trip.id}`).then((r) => { setPhotos(r.data.items); setRole(r.data.my_role); }).catch(() => {});
  useEffect(() => { load(); }, [trip.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // poll while PhotoPulse AI is analyzing any photo
  const analyzing = photos.some((p) => p.photopulse?.status === "analyzing");
  useEffect(() => {
    if (!analyzing) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [analyzing]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (e) => {
    const files = [...(e.target.files || [])].slice(0, 20);
    if (!files.length) return;
    const ok = files.filter((f) => f.size <= 12 * 1024 * 1024);
    const tooBig = files.length - ok.length;
    if (!ok.length) {
      toast.error("Each photo must be 12 MB or smaller");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      ok.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/album/${trip.id}/photos`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      const n = data.created.length;
      const skipped = tooBig + (ok.length - n);
      toast.success(`${n} of ${files.length} photo${files.length > 1 ? "s" : ""} added` +
        (skipped ? ` — ${skipped} skipped (unsupported type or over 12 MB)` : "") +
        (data.created.some((p) => p.lat) ? " · GPS location read from the photo" : "") +
        (data.privacy?.exif_stripped ? " · hidden metadata (EXIF/GPS) removed" : "") +
        (data.photopulse_enabled ? " · sent to PhotoPulse AI" : ""));
      (data.warnings || []).forEach((w) => toast.warning(w, { duration: 9000 }));
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (p) => {
    if (!window.confirm("Delete this photo and its comments?")) return;
    await api.delete(`/album/photos/${p.id}`);
    setOpen(null); load();
  };

  const canEdit = role !== "view";
  return (
    <section id="photos" className="mb-10 scroll-mt-24" data-testid="trip-album">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <h2 className="font-heading font-900 text-emerald-700 text-2xl flex items-center gap-2">
          <Images className="w-6 h-6" /> Photos <span className="text-sm font-mono text-muted-foreground">({photos.length})</span>
        </h2>
        {canEdit && (
          <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="album-upload-btn"
            className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {busy ? "Uploading…" : "Add photos"}
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple capture="environment" className="hidden" onChange={upload} data-testid="album-upload-input" />
      </div>
      <p className="text-sm text-muted-foreground mb-3" data-testid="trip-photos-caption">
        Upload and keep your travel photos for this trip — they stay saved here under the trip, with captions, locations and comments.
      </p>
      {photos.length === 0 ? (
        <div className="border border-dashed border-zinc-300 p-8 text-center text-sm text-muted-foreground">
          Attach memories to this trip — upload several photos at once (or use your camera). Date and GPS location are read automatically when the photo has them.
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="album-grid">
          {photos.map((p) => (
            <button key={p.id} onClick={() => setOpen(p)} data-testid={`album-photo-${p.id}`} className="relative group aspect-square overflow-hidden border border-zinc-200 hover:border-emerald-500">
              <img src={src(p.url)} alt={p.title || "trip photo"} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              {p.photopulse?.status === "analyzing" && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-violet-500 animate-pulse" title="PhotoPulse AI analyzing…" data-testid={`pp-analyzing-${p.id}`} />
              )}
              {(p.privacy_guard?.codes?.length || 0) > 0 && (
                <span className="absolute top-1 left-1 bg-amber-500 text-white text-[9px] font-800 px-1 py-0.5 leading-none"
                  title="Scannable code detected (boarding pass / ticket?) — avoid sharing this photo publicly"
                  data-testid={`album-code-warning-${p.id}`}>CODE</span>
              )}
              {(p.title || p.comments?.length > 0) && (
                <span className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-1.5 py-1 text-left truncate">
                  {p.title || ""}{p.comments?.length > 0 && ` 💬${p.comments.length}`}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {open && <Lightbox photo={photos.find((x) => x.id === open.id) || open} canEdit={canEdit}
        onClose={() => setOpen(null)} onEdit={() => setEditing(open)} onDelete={() => del(open)} onChanged={load} />}
      {editing && <PhotoEdit photo={editing} onClose={() => setEditing(null)}
        onSaved={(p) => { setEditing(null); setOpen(p); load(); }} />}
    </section>
  );
}

function Lightbox({ photo: p, canEdit, onClose, onEdit, onDelete, onChanged }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const pp = p.photopulse || {};
  const rerun = async () => {
    try {
      await api.post(`/album/photos/${p.id}/photopulse`);
      toast.success("Sent to PhotoPulse AI — location & tags will appear shortly");
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "PhotoPulse is unavailable"); }
  };
  const comment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/album/photos/${p.id}/comments`, { text }); setText(""); onChanged(); }
    catch { toast.error("Could not add comment"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/90 overflow-y-auto" data-testid="album-lightbox" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex justify-end gap-2 mb-2">
          {canEdit && <button onClick={onEdit} data-testid="lightbox-edit" className="p-2 text-zinc-300 hover:text-white"><Pencil className="w-5 h-5" /></button>}
          {canEdit && <button onClick={onDelete} data-testid="lightbox-delete" className="p-2 text-zinc-300 hover:text-red-400"><Trash2 className="w-5 h-5" /></button>}
          <button onClick={onClose} data-testid="lightbox-close" className="p-2 text-zinc-300 hover:text-white"><X className="w-6 h-6" /></button>
        </div>
        <img src={src(p.url)} alt={p.title || ""} className="max-h-[60vh] mx-auto object-contain" />
        <div className="bg-white mt-3 p-4">
          {p.title && <div className="font-heading font-900 text-lg" data-testid="lightbox-title">{p.title}</div>}
          {p.description && <p className="text-sm mt-1">{p.description}</p>}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-2">
            {p.taken_at && <span className="inline-flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" />{p.taken_at.replace("T", " ")}</span>}
            {(p.location || p.lat != null) && (
              <span className="inline-flex items-center gap-1" data-testid="lightbox-location">
                <MapPin className="w-3.5 h-3.5" />{p.location || ""}{p.lat != null && (
                  <a href={`https://www.google.com/maps?q=${p.lat},${p.lng}`} target="_blank" rel="noreferrer" className="text-primary hover:underline font-mono">
                    {p.lat}, {p.lng}
                  </a>)}
              </span>)}
          </div>
          {(p.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {p.tags.map((t) => <span key={t} className="inline-flex items-center gap-0.5 text-[10px] font-bold bg-emerald-50 text-emerald-700 px-1.5 py-0.5"><Tag className="w-3 h-3" />{t}</span>)}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="pp-status-row">
            {pp.status === "analyzing" && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-violet-50 text-violet-700 px-1.5 py-0.5 animate-pulse" data-testid="pp-chip-analyzing">
                <Sparkles className="w-3 h-3" /> PhotoPulse AI analyzing…
              </span>)}
            {pp.status === "approved" && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-violet-50 text-violet-700 px-1.5 py-0.5" data-testid="pp-chip-approved">
                <Sparkles className="w-3 h-3" /> AI-enriched by PhotoPulse{pp.confidence != null ? ` · ${pp.confidence}%` : ""}
              </span>)}
            {pp.status === "needs_review" && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-amber-50 text-amber-700 px-1.5 py-0.5" data-testid="pp-chip-review">
                <Sparkles className="w-3 h-3" /> Awaiting your review in PhotoPulse
              </span>)}
            {pp.status === "failed" && (
              <span className="text-[10px] font-bold bg-red-50 text-red-600 px-1.5 py-0.5" data-testid="pp-chip-failed">PhotoPulse enrichment failed</span>)}
            {canEdit && pp.status !== "analyzing" && (
              <button onClick={rerun} data-testid="pp-rerun-btn"
                className="inline-flex items-center gap-1 text-[10px] font-bold border border-violet-300 text-violet-700 px-1.5 py-0.5 hover:bg-violet-50">
                <Sparkles className="w-3 h-3" /> {pp.status ? "Re-run AI enrichment" : "AI enrich (PhotoPulse)"}
              </button>)}
          </div>
          <div className="border-t border-zinc-100 mt-3 pt-3">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1">
              <MessageCircle className="w-3.5 h-3.5" /> Comments ({(p.comments || []).length})
            </div>
            {(p.comments || []).map((c) => (
              <div key={c.id} className="text-sm mb-1.5" data-testid={`comment-${c.id}`}>
                <span className="font-bold">{c.author}</span> <span className="text-[10px] text-muted-foreground">{c.at?.slice(0, 10)}</span>
                <div>{c.text}</div>
              </div>
            ))}
            <form onSubmit={comment} className="flex gap-2 mt-2">
              <input value={text} onChange={(e) => setText(e.target.value)} data-testid="comment-input"
                placeholder="Add a comment…" className="flex-1 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
              <button type="submit" disabled={busy || !text.trim()} data-testid="comment-send"
                className="px-3 py-2 bg-emerald-600 text-white disabled:opacity-50"><Send className="w-4 h-4" /></button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function PhotoEdit({ photo, onClose, onSaved }) {
  const [f, setF] = useState({ title: photo.title || "", description: photo.description || "",
    taken_at: photo.taken_at || "", location: photo.location || "", lat: photo.lat ?? "", lng: photo.lng ?? "",
    tags: (photo.tags || []).join(", ") });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/album/photos/${photo.id}`, {
        ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) });
      toast.success("Photo details saved");
      onSaved(data);
    } catch { toast.error("Could not save"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Photo details" onClose={onClose}>
      <div className="space-y-3" data-testid="photo-edit-form">
        <div><label className={lbl}>Title</label><input value={f.title} onChange={set("title")} data-testid="photo-title" className={inp} placeholder="Sunset over the Golden Gate" /></div>
        <div><label className={lbl}>Description</label><textarea value={f.description} onChange={set("description")} data-testid="photo-desc" rows={2} className={inp} /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Taken (date & time)</label><input type="datetime-local" value={f.taken_at} onChange={set("taken_at")} data-testid="photo-taken" className={inp} /></div>
          <div><label className={lbl}>Location</label><input value={f.location} onChange={set("location")} data-testid="photo-location" className={inp} placeholder="Eiffel Tower, Paris" /></div>
          <div><label className={lbl}>GPS latitude / longitude</label>
            <div className="flex gap-2">
              <input value={f.lat} onChange={set("lat")} data-testid="photo-lat" className={inp} placeholder="48.8584" />
              <input value={f.lng} onChange={set("lng")} data-testid="photo-lng" className={inp} placeholder="2.2945" />
            </div></div>
          <div><label className={lbl}>Tags (comma separated)</label><input value={f.tags} onChange={set("tags")} data-testid="photo-tags" className={inp} placeholder="beach, kids, food" /></div>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-300">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="photo-save" className="px-5 py-2 text-sm font-bold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
            {busy ? "Saving…" : "Save details"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
